import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../services/api';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [unverifiedBuyers, setUnverifiedBuyers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin') {
      navigate('/');
    } else {
      fetchUnverifiedBuyers();
      fetchAllProducts();
    }
  }, [user, navigate]);

  const fetchUnverifiedBuyers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/buyers/unverified`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setUnverifiedBuyers(data.data.buyers);
      }
    } catch (error) {
      console.error('Error fetching buyers:', error);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.status === 'success') {
        setProducts(data.data.products);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleVerifyBuyer = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/buyers/${id}/verify`, {
        method: 'PUT',
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.status === 'success') {
        toast({
          title: 'Buyer verified',
          status: 'success',
          duration: 3000,
        });
        setUnverifiedBuyers(unverifiedBuyers.filter((b) => b._id !== id));
      }
    } catch (error) {
      toast({
        title: 'Error verifying buyer',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (response.ok) {
        toast({
          title: 'Product deleted',
          status: 'success',
          duration: 3000,
        });
        setProducts(products.filter((p) => p._id !== id));
      }
    } catch (error) {
      toast({
        title: 'Error deleting product',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <Box minH="100vh" bg="neutral.50" py={8}>
      <Container maxW="container.xl">
        <VStack align="stretch" spacing={8}>
          <Heading as="h1" size="xl" color="neutral.900">
            Admin Dashboard
          </Heading>

          <Box bg="white" borderRadius="xl" boxShadow="sm" p={6}>
            <Tabs colorScheme="primary">
              <TabList>
                <Tab>Verify Buyers</Tab>
                <Tab>Manage Products</Tab>
              </TabList>

              <TabPanels>
                <TabPanel>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>Business Name</Th>
                        <Th>Location</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {unverifiedBuyers.map((buyer) => (
                        <Tr key={buyer._id}>
                          <Td>{buyer.fullName}</Td>
                          <Td>{buyer.email}</Td>
                          <Td>{buyer.businessName}</Td>
                          <Td>{buyer.farmLocation || 'N/A'}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="green"
                              onClick={() => handleVerifyBuyer(buyer._id)}
                            >
                              Verify
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                      {unverifiedBuyers.length === 0 && (
                        <Tr>
                          <Td colSpan={5} textAlign="center">
                            No unverified buyers found.
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TabPanel>

                <TabPanel>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Product</Th>
                        <Th>Category</Th>
                        <Th>Price</Th>
                        <Th>Farmer</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {products.map((product) => (
                        <Tr key={product._id}>
                          <Td>{product.name}</Td>
                          <Td>
                            <Badge>{product.category}</Badge>
                          </Td>
                          <Td>${product.price}/{product.unit}</Td>
                          <Td>{product.farmerName}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="red"
                              onClick={() => handleDeleteProduct(product._id)}
                            >
                              Remove
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
